import React from 'react'
import { useTheme } from '@mui/styles'
import { without } from 'lodash'
import { ObjectLiteral } from 'interfaces'
import { Tree } from 'components/ui-layouts/transferList/tree/Tree'
import { Icon } from '@ketch-com/deck'
import { Box } from '@mui/material'

type Props<T> = {
  excludedTitle: string
  /** Excluded items section title */
  excludedItems: T[]
  /** Excluded items section search string */
  excludedItemsSearchString?: string
  includedTitle: string
  /** Included items section items */
  includedItems: T[]
  /** Included items section search string */
  includedItemsSearchString?: string
  itemRenderer: (item: T, searchString?: string) => React.ReactNode | React.ReactNode[]
  /** Component onChange method - returns updated list of included items */
  onChange: (nextIncludedItems: T[], nextIncludedItem: T, key: string) => void
  /** Component onSearch method - returns search string */
  onSearchIncludedItems?: (nextSearchString: string) => void
  /** Component onSearch method - returns search string */
  onSearchExcludedItems?: (nextSearchString: string) => void
  /** Component custom className */
  className?: string
  /** Defines what key is an expandable key */
  expandableKey?: string
}

export function TransferList<T extends ObjectLiteral>(props: Props<T>) {
  const theme = useTheme()
  const {
    excludedTitle,
    excludedItems,
    includedTitle,
    includedItems,
    className,
    onChange,
    onSearchExcludedItems,
    onSearchIncludedItems,
    itemRenderer,
    excludedItemsSearchString,
    includedItemsSearchString,
    expandableKey,
  } = props

  const handleIncludeItem = (nextItem: T) => {
    onChange([...includedItems, nextItem], nextItem, 'INCLUDE')
  }

  const handleExcludeItem = (nextItem: T) => {
    onChange(without(includedItems, nextItem), nextItem, 'EXCLUDE')
  }

  return (
    <Box display="flex" className={className}>
      <Tree
        expandableKey={expandableKey}
        type="EXCLUDE"
        itemRenderer={itemRenderer}
        title={excludedTitle}
        items={excludedItems}
        searchString={excludedItemsSearchString}
        onSearch={onSearchExcludedItems}
        onItemClick={handleIncludeItem}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          height: 430,
          width: 130,
        }}
      >
        <Icon name="OArrowCRight" iconColor={theme.palette.Text.Secondary} />
        <Icon name="OArrowCLeft" iconColor={theme.palette.Text.Secondary} />
      </Box>

      <Tree
        expandableKey={expandableKey}
        type="INCLUDE"
        itemRenderer={itemRenderer}
        title={includedTitle}
        items={includedItems}
        searchString={includedItemsSearchString}
        onSearch={onSearchIncludedItems}
        onItemClick={handleExcludeItem}
      />
    </Box>
  )
}
